import { useMemo } from 'react';
import styled from 'styled-components';
import {
  Breadcrumbs as BreadcrumbsOrigin,
  Flex,
  FlexColumn,
} from '@elementary/common-ui/components';
import { useBreadcrumbs } from '@elementary/common-ui/hooks/useBreadcrumbs';
import useCriticalAssets from '@elementary/common-ui/hooks/useCriticalAssets';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import { useLocation } from 'react-router-dom';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import AddTestDialogBase from 'shared/components/AddTestWizard/AddTestDialog';
import { AssetDetails } from 'shared/components/Asset';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import useCriticalAssetsNotificationBanner from 'catalog/hooks/useCriticalAssetsNotificationBanner';
import { WizardMode } from 'testManagement/types';
import { ADD_TEST_DIALOG_MODE } from '@elementary/common-ui/constants/urlParams';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import AiAccessAlert from './AiAccessAlert';
import CatalogHeader from './CatalogHeader';
import CatalogTable from './CatalogTable';
import CoverageTable from './CoverageTable';
import CriticalAssetsNotification from './CriticalAssetsNotification';

const Wrapper = styled(FlexColumn)`
  height: 100%;
  width: 100%;
  padding: 0 24px 0 24px;
  gap: 32px;
  overflow: auto;
`;

const AssetViewWrapper = styled(Wrapper)`
  overflow: hidden;
`;

const StickySection = styled(FlexColumn)`
  background: ${({ theme }) => theme.palette.common.white};
  gap: 16px;
  padding-top: 24px;
  position: sticky;
  top: 0;
  z-index: 4;
`;

const Breadcrumbs = styled(BreadcrumbsOrigin)<{ $spaceTop?: boolean }>`
  margin-top: ${({ $spaceTop }) => ($spaceTop ? '24px' : '0')};
  transition: all 0.2s ease-in-out;
`;

const AddTestDialog = ({
  addTestDialogMode,
}: {
  addTestDialogMode: WizardMode | undefined;
}) => {
  const [, setSearchParams] = useSearchParams();
  const hasPermission = useEnvPermission();

  if (!hasPermission('EDIT_TEST_CONFIGURATIONS')) {
    return null;
  }

  const clearDialogUrlParam = () =>
    // using a timeout since the dialog also sets url params on close.
    // and they override each other
    setTimeout(() => {
      setSearchParams(() => {
        const newParams = new URLSearchParams(window.location.search);
        newParams.delete(ADD_TEST_DIALOG_MODE);
        return newParams;
      });
    }, 50);

  return (
    <AddTestDialogBase
      open={!!addTestDialogMode}
      mode={addTestDialogMode}
      onClose={clearDialogUrlParam}
    />
  );
};

const Catalog = () => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigateWithEnv();
  const { selectedTreeNode } = useSelectedTreeItem();
  const addTestDialogMode: WizardMode | undefined = useMemo(
    () => searchParams.get(ADD_TEST_DIALOG_MODE) as WizardMode,
    [searchParams],
  );

  const selectedTab = useMemo(() => {
    const split = pathname.split('/');
    const pageName = split[split.length - 1];
    return pageName === 'coverage' ? 'coverage' : 'metadata';
  }, [pathname]);

  const breadcrumbs = useBreadcrumbs({
    pageTitle: 'Catalog',
  });

  const modelId = useMemo(
    () =>
      selectedTreeNode && selectedTreeNode.resourceType !== 'folder'
        ? selectedTreeNode.id
        : undefined,
    [selectedTreeNode],
  );

  const isCriticalAssetsIds =
    modelId && selectedTreeNode?.resourceType !== 'test' ? [modelId] : [];
  const {
    criticalAssetsData,
    hasSetPermissions: hasSetCriticalAssetsPermissions,
  } = useCriticalAssets(
    isCriticalAssetsIds ? { ids: isCriticalAssetsIds } : {},
  );
  const { data: criticalAsset } = criticalAssetsData;
  const isCritical = criticalAsset?.[0]?.isCritical || false;

  const { showBanner, setShowNotificationsBanner } =
    useCriticalAssetsNotificationBanner(isCritical);

  const isSingleResourceSelected = useMemo(() => {
    if (selectedTreeNode && selectedTreeNode.resourceType !== 'folder')
      return true;
    if (selectedTreeNode?.resourceType === 'folder') return false;
    return !!selectedTreeNode;
  }, [selectedTreeNode]);

  if (!isSingleResourceSelected) {
    return (
      <FlexColumn sx={{ height: '100%' }}>
        <AiAccessAlert />
        <Wrapper>
          <StickySection>
            <CatalogHeader
              selectedTab={selectedTab}
              onChange={(val) => {
                const newParams = new URLSearchParams(searchParams);
                navigate(
                  `/catalog/${val.toLowerCase()}?${newParams.toString()}`,
                  { replace: true },
                );
              }}
            />
          </StickySection>

          {selectedTab === 'metadata' ? <CatalogTable /> : <CoverageTable />}
          <AddTestDialog addTestDialogMode={addTestDialogMode} />
        </Wrapper>
      </FlexColumn>
    );
  }

  return (
    <AssetViewWrapper>
      <StickySection>
        <CriticalAssetsNotification
          visible={showBanner}
          hasSetPermissions={hasSetCriticalAssetsPermissions}
          onClose={() => setShowNotificationsBanner(false)}
        />

        <Breadcrumbs items={breadcrumbs} $spaceTop={showBanner} />
      </StickySection>

      <Flex sx={{ overflow: 'auto', pr: 2, flex: 1, height: '100%' }}>
        <AssetDetails
          id={modelId}
          isTest={selectedTreeNode?.resourceType === 'test'}
          showActions
          enableEditDependencies
        />
      </Flex>

      <AddTestDialog addTestDialogMode={addTestDialogMode} />
    </AssetViewWrapper>
  );
};

export default Catalog;
